import axios from 'axios';
import { EventCustomFeedbackCodes, EventLevels, EventTypes } from 'defs/events';
import { SpringAPI } from 'defs/http/api';
import { TIMEOUT, WITH_CREDENTIALS } from 'defs/http/config';
import { group, groupEnd, log } from 'helper/debug';
import appConfig from 'helper/mapBackendConfig';
import { reload } from 'lib/ApplicationLoader';
import { attach as getSharedEvents } from 'lib/SharedEvents';
import { actionCreators as connectionAC } from 'store/connection';
import { actionTypes as eventsAT } from 'store/events';

// Simplify URL for Spring API
const URL_SPRING_API: string = appConfig.env.urlSpringAPI;

type StoreTypeDispatchOpts = {
	type: string;
	events?: [
		{
			eventLevel: string;
			code: string;
			eventType: string;
		}
	];
};

export type StoreType = {
	dispatch(opts: StoreTypeDispatchOpts): () => void;
};

type AxiosInternalErrorRes = {
	code?: string;
	response?: {
		response: {
			status: number;
		};
	};
	config: {
		url: string;
		method: 'get' | 'put' | 'post' | 'delete' | 'patch';
		timeout: number;
		withCredentials?: boolean;
	};
};

type AxiosInternlDataRes = {
	data: Promise<any>;
};

const AXIOS_CODES = {
	TIMEOUT_REACHED: 'ECONNABORTED',
};

const setAxiosConfig = (store: StoreType): void => {
	group('Axios Configuration');

	// Credentials
	axios.defaults.withCredentials = WITH_CREDENTIALS;

	// Set default timeout to 10 seconds
	axios.defaults.timeout = TIMEOUT;

	log(`Using Credentials: ${WITH_CREDENTIALS ? 'Yes' : 'No'}`);
	log(`Default Timeout: ${axios.defaults.timeout}`);

	groupEnd('Axios Configuration');

	// Interceptor
	// - https://github.com/mzabriskie/axios#interceptors
	axios.interceptors.response.use(
		// success
		(response: AxiosInternlDataRes): any => {
			return response.data;
		},
		// error
		(err) => {
			const { code, response } = err;
			let config = err.config;

			config = config || {
				method: '?',
			};

			if (code === void 0 && response === void 0) {
				// This error happens when the user goes offline while a request is pending
				getSharedEvents().emit('http:error', 'EUNKNOWN', {}, config);

				if (config.url.indexOf(':8099/trace') >= 0) {
					// Prevent offline screen if trace requests failed
					return null;
				}

				// Save network connection error in redux store
				return store.dispatch(
					connectionAC.doTrackNetworkInterceptionError({
						url: config.url,
						method: config.method,
						withCredentials: !!config.withCredentials,
						timestamp: new Date().toISOString(),
					})
				);
			}

			log(
				[
					`HTTP ${config.method.toUpperCase()} failed: ${code} while requesting ${config.url}`,
					` [${response ? response.status : '-1'}]`,
				].join(' ')
			);

			// Timeout was reached, show error to the user
			if (code && code === AXIOS_CODES.TIMEOUT_REACHED) {
				// Prevent to show message on notification fetch timeouts because they are irrelevant
				if (config.url && config.url !== SpringAPI.notifications.getNotifications()) {
					getSharedEvents().emit('http:timeout', code, response, config);

					store.dispatch({
						type: eventsAT.REQUEST_ADD_EVENTS,
						events: [
							{
								eventLevel: EventLevels.NotAvailable,
								code: EventCustomFeedbackCodes.Error408,
								eventType: EventTypes.Feedback,
							},
						],
					});
				}
			}

			// Check for Spring API 401 Unauthorized
			if (response && response.status === 401 && config.url.includes(URL_SPRING_API)) {
				reload();
			}

			// Check for 500er Error
			if (response && response.status === 500) {
				if (config.url && config.url !== SpringAPI.notifications.getNotifications()) {
					getSharedEvents().emit('http:error', code, response, config);

					store.dispatch({
						type: eventsAT.REQUEST_ADD_EVENTS,
						events: [
							{
								eventLevel: EventLevels.NotAvailable,
								code: EventCustomFeedbackCodes.Error500,
								eventType: EventTypes.Feedback,
							},
						],
					});
				}
			}

			// Return the original error
			return Promise.reject(err);
		}
	);
};

export default setAxiosConfig;
